import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "leaflet/dist/leaflet.css";
import App from "@/App";

// PWA UPDATE STRATEGY
// -------------------
// Two audiences to handle:
//   1. Users who ALREADY have the app installed (PWA) — must auto-detect new
//      releases and either update silently or via a toast prompt.
//   2. Users landing on the site for the first time — must always receive the
//      latest bundle. Handled by the network-first SW fetch handler + hashed
//      CRA assets (no manual work needed here).
//
// Layers of defense for installed users:
//   • Service Worker registered with `updateViaCache: 'none'` so the browser
//     always re-fetches the SW file from the network on register/update calls.
//   • `reg.update()` fires on: page load, visibility→visible, focus, every 15
//     minutes, AND on every history navigation (patched below).
//   • The `updatefound` event dispatches `orio:sw-update-ready` — caught by
//     `SwUpdateToast` which offers a one-tap update.
//   • Server-side version check: every 3 minutes we poll `/api/app/version`.
//     If the server reports a version newer than what the client cached at
//     boot, we call `reg.update()` immediately (and log a warning). The client
//     then either auto-updates (if no active work) or shows the toast.
if ("serviceWorker" in navigator) {
  const isDev = process.env.NODE_ENV !== "production";
  window.addEventListener("load", () => {
    if (isDev) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister().catch(() => {}));
      }).catch(() => {});
      if (window.caches && caches.keys) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
      }
      return;
    }
    navigator.serviceWorker
      .register("/service-worker.js", { updateViaCache: "none" })
      .then((reg) => {
        window.__orioSwReg = reg;

        // Kick a check immediately on boot — catches releases published while
        // the previous session was closed.
        reg.update().catch(() => {});

        // Periodic checks — every 15 minutes for long-running PWA sessions.
        const check = () => reg.update().catch(() => {});
        setInterval(check, 15 * 60 * 1000);

        // Check whenever the user returns to the tab or the window regains focus.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
        window.addEventListener("focus", check);

        // Check on every SPA route change too (patches pushState / replaceState).
        const patch = (name) => {
          const orig = window.history[name];
          window.history[name] = function () {
            const r = orig.apply(this, arguments);
            check();
            return r;
          };
        };
        patch("pushState");
        patch("replaceState");
        window.addEventListener("popstate", check);

        // When a new SW is installed & waiting, prompt via a global event the UI can catch
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent("orio:sw-update-ready", { detail: { reg } }));
            }
          });
        });

        // Backend version poll — cross-checks the SW cadence and catches cases
        // where the SW file check misses (Wi-Fi issues, CDN cache, etc.).
        // Uses a lightweight unauthenticated GET.
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        let bootVersion = null;
        const versionCheck = async () => {
          try {
            const r = await fetch(`${backend}/api/app/version`, { cache: "no-store" });
            if (!r.ok) return;
            const j = await r.json();
            if (!j?.version) return;
            if (bootVersion === null) {
              bootVersion = j.version;
              try { localStorage.setItem("orio_boot_version", bootVersion); } catch {}
              return;
            }
            if (j.version !== bootVersion) {
              // Server has a fresher version — force SW to re-check right now.
              reg.update().catch(() => {});
            }
          } catch { /* offline, ignore */ }
        };
        versionCheck();
        setInterval(versionCheck, 3 * 60 * 1000);
      })
      .catch(() => {});

    // Reload the page automatically if the controlling SW switches.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      setTimeout(() => window.location.reload(), 500);
    });
  });
}

// Global manual "check for updates" helper — exposed so a Settings button can
// call it and immediately verify + prompt.
window.orioCheckForUpdate = async () => {
  try {
    const reg = window.__orioSwReg || (await navigator.serviceWorker?.getRegistration?.());
    if (!reg) return { ok: false, reason: "no-sw" };
    await reg.update();
    if (reg.waiting) {
      window.dispatchEvent(new CustomEvent("orio:sw-update-ready", { detail: { reg } }));
      return { ok: true, updated: true };
    }
    return { ok: true, updated: false };
  } catch (e) {
    return { ok: false, reason: String(e) };
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);
